// Polyfill crypto.randomUUID for non-secure contexts (HTTP)
if (typeof crypto !== "undefined" && typeof crypto.randomUUID !== "function") {
  crypto.randomUUID =
    function randomUUID(): `${string}-${string}-${string}-${string}-${string}` {
      return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
        (
          +c ^
          (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))
        ).toString(16),
      ) as `${string}-${string}-${string}-${string}-${string}`;
    };
}

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { I18nProvider } from "./i18n";
import App from "./App";
import "./styles/index.css";
import "./lib/pwa";

// After a deploy, open tabs hold an index.html that references chunks
// (via React.lazy / dynamic import) whose hashes are no longer on disk
// OR whose SW-cached response was poisoned (see 2026-04-23 incident:
// nginx SPA fallback served index.html HTML for a missing asset during
// deploy window → workbox cached it as valid 200 for that URL → all
// subsequent loads try to parse HTML as ES module).
//
// Recovery: unregister SW + drop all caches, then reload.
// Without the unregister step, the poisoned SW just serves the bad
// cached response again after reload.
//
// IMPORTANT: do NOT call event.preventDefault(). Vite's source:
//   if (!e.defaultPrevented) throw cause;
// With preventDefault the dynamic import's promise resolves with
// `undefined` instead of rejecting. React.lazy then reads `.default`
// on undefined → TypeError.
window.addEventListener("vite:preloadError", () => {
  const RELOAD_KEY = "vite-preload-reload-ts";
  const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? "0");
  if (Date.now() - last < 10000) return; // prevent loop (wider window for SW flush)
  sessionStorage.setItem(RELOAD_KEY, String(Date.now()));

  void (async () => {
    try {
      // 1. Unregister all service workers
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      // 2. Clear all CacheStorage (workbox precache, runtime caches)
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch {
      // Best-effort — reload anyway
    }
    window.location.reload();
  })();
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <I18nProvider>
      <App />
    </I18nProvider>
  </StrictMode>,
);
